import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Plus, Shield, Trash2, Users, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_BADGES = {
  super_admin: 'bg-[#1f2a4d] text-white',
  internal: 'bg-slate-900 text-white',
  client_admin: 'bg-red-50 text-red-800 border border-red-100',
  client_viewer: 'bg-slate-50 text-slate-700 border border-slate-200',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  internal: 'Internal Team',
  client_admin: 'Client Admin',
  client_viewer: 'Client Viewer',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    username: '', password: '', full_name: '', role: 'internal', client_id: ''
  });

  const fetchData = async () => {
    try {
      const [usersData, clientsData] = await Promise.all([
        api('/api/auth/users'),
        api('/api/clients')
      ]);
      setUsers(usersData);
      setClients(clientsData);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (!payload.client_id || payload.role === 'super_admin' || payload.role === 'internal') {
        payload.client_id = null;
      }
      await api('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      toast.success('User created successfully');
      setDialogOpen(false);
      setForm({ username: '', password: '', full_name: '', role: 'internal', client_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleNotification = async (userId, field, currentValue) => {
    try {
      await api(`/api/auth/users/${userId}/notifications`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: !currentValue })
      });
      toast.success(`Notification ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };


  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="text-center py-20">
        <Shield size={40} className="mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium mb-2">Access Denied</h3>
        <p className="text-sm text-muted-foreground">Only Super Admins can manage users</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            User Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage team and client user accounts
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-user-button" style={{ background: '#1f2a4d' }}>
          <Plus size={16} className="mr-2" /> Add User
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-center">Sentiment Alerts</TableHead>
                <TableHead className="text-center">Report Alerts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const clientName = u.client_id ? clients.find(c => c._id === u.client_id)?.name : '—';
                const isTeam = u.role === 'super_admin' || u.role === 'internal';
                return (
                  <TableRow key={u._id}>
                    <TableCell className="font-medium">{u.full_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{u.email || u.username}</TableCell>
                    <TableCell>
                      <Badge className={ROLE_BADGES[u.role] || ''}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{clientName}</TableCell>
                    <TableCell className="text-center">
                      {isTeam && (
                        <Button
                          variant="ghost" size="sm"
                          className={`h-7 text-xs ${u.sentiment_alerts ? 'text-red-600' : 'text-muted-foreground'}`}
                          onClick={() => toggleNotification(u._id, 'sentiment_alerts', u.sentiment_alerts)}
                        >
                          {u.sentiment_alerts ? <><Bell size={13} className="mr-1" /> On</> : <><BellOff size={13} className="mr-1" /> Off</>}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {isTeam && (
                        <Button
                          variant="ghost" size="sm"
                          className={`h-7 text-xs ${u.report_alerts ? 'text-blue-600' : 'text-muted-foreground'}`}
                          onClick={() => toggleNotification(u._id, 'report_alerts', u.report_alerts)}
                        >
                          {u.report_alerts ? <><Bell size={13} className="mr-1" /> On</> : <><BellOff size={13} className="mr-1" /> Off</>}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <p className="text-sm text-muted-foreground">Add a new team member or client user account</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                data-testid="user-fullname-input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input
                data-testid="user-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="jsmith"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                data-testid="user-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(val) => setForm({ ...form, role: val })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="internal">Internal Team</SelectItem>
                  <SelectItem value="client_admin">Client Admin</SelectItem>
                  <SelectItem value="client_viewer">Client Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.role === 'client_admin' || form.role === 'client_viewer') && (
              <div className="space-y-2">
                <Label>Assigned Client</Label>
                <Select value={form.client_id} onValueChange={(val) => setForm({ ...form, client_id: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!form.username || !form.password || !form.full_name}
              data-testid="user-save-button"
              style={{ background: '#1f2a4d' }}
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
